import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';

import Type from '../Typography';
import { call, restProps, pluckProps } from '../utils';

import Close, { Context } from './Close';
import Dialog from './Dialog';
import StatusIcon from './StatusIcon';
import styles from './Prompt.less';

const cxb = cx.bind(styles);

/**
 * **Access:**
 * ```js
 * import {Prompt} from '@iss/client-react-components'
 * ```
 *
 */
export default class Prompt extends React.PureComponent {
  static Modal = React.memo(
    React.forwardRef(
      ({ show = true, onDismiss, ...props }, ref) =>
        show && (
          <Dialog onBeforeDismiss={onDismiss} {...pluckProps(Dialog, props)}>
            <Prompt {...restProps(Dialog, props)} ref={ref} />
          </Dialog>
        )
    )
  );

  static contextType = Context;

  static propTypes = {
    icon: PropTypes.node,
    onDismiss: PropTypes.func,
    title: PropTypes.string,
    type: PropTypes.oneOf(['info', 'attention', 'warning', 'success']),
    classes: PropTypes.shape({
      root: PropTypes.string,
      title: PropTypes.string,
      content: PropTypes.string,
      icon: PropTypes.string,
    }),
  };

  close() {
    const { onDismiss } = this.props;

    if (!this.context && !onDismiss) {
      console.warn('Prompt.close() called without context. ');
    }

    call(onDismiss || this.context);
  }

  render() {
    const { children, className, classes, icon, onDismiss, title, type, ...props } = this.props;

    const { root: rootClass, title: titleClass, content: contentClass, icon: iconClass } = classes || {};

    const remaps = {
      //design uses the attention icon on info prompts.
      info: 'attention',
    };

    return (
      <div className={cxb('root', type, className, rootClass)} {...props}>
        <Type.Heading2 className={cxb('title', titleClass, { 'custom-icon': icon })}>
          {icon ? (
            <span className={cxb('custom-icon', iconClass)}>
              {typeof icon !== 'string' ? icon : <img alt="" src={icon} />}
            </span>
          ) : (
            <StatusIcon className={cxb('icon', iconClass)} type={remaps[type] || type} />
          )}
          {title}
          <Close className={cxb('close')} onClick={onDismiss} />
        </Type.Heading2>
        <div className={cxb('content', contentClass)}>{children}</div>
      </div>
    );
  }
}
